import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Chart, ArcElement, Tooltip, Legend, DoughnutController } from 'chart.js';
import './Tokenomics.css';

Chart.register(ArcElement, Tooltip, Legend, DoughnutController);

const Tokenomics = () => {
  const chartRef = useRef();
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const tokenomicsData = [
    { label: 'Public Sale', percentage: 40, color: '#00d4ff', amount: '400,000,000' },
    { label: 'Ecosystem Development', percentage: 25, color: '#ff6b6b', amount: '250,000,000' },
    { label: 'Staking Rewards', percentage: 20, color: '#4ecdc4', amount: '200,000,000' },
    { label: 'Team & Advisors', percentage: 10, color: '#ffe66d', amount: '100,000,000' },
    { label: 'Marketing & Partnerships', percentage: 5, color: '#ff8b94', amount: '50,000,000' }
  ];

  const tokenInfo = [
    { label: 'Total Supply', value: '1,000,000,000 TERRA' },
    { label: 'Circulating Supply', value: '400,000,000 TERRA' },
    { label: 'Market Cap', value: '$50,000,000' },
    { label: 'Token Price', value: '$0.125' }
  ];

  useEffect(() => {
    if (inView && chartRef.current) {
      const ctx = chartRef.current.getContext('2d');
      
      // Clear any existing chart
      if (chartRef.current.chart) {
        chartRef.current.chart.destroy();
      }

      const chart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: tokenomicsData.map(item => item.label),
          datasets: [{
            data: tokenomicsData.map(item => item.percentage),
            backgroundColor: tokenomicsData.map(item => item.color),
            borderWidth: 0,
            borderRadius: 8,
            spacing: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              titleColor: '#fff',
              bodyColor: '#fff',
              borderColor: '#00d4ff',
              borderWidth: 1,
              cornerRadius: 8,
              callbacks: {
                label: function(context) {
                  return `${context.label}: ${context.parsed}% (${tokenomicsData[context.dataIndex].amount} TERRA)`;
                }
              }
            }
          },
          cutout: '60%',
          animation: {
            animateRotate: true,
            animateScale: true,
            duration: 2000,
            easing: 'easeOutQuart'
          }
        }
      });

      chartRef.current.chart = chart;
    }
  }, [inView]);

  return (
    <section id="tokenomics" className="tokenomics" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title">Tokenomics</h2>
          <p className="section-subtitle">Designed for sustainable growth and community prosperity</p>
        </motion.div>

        <div className="tokenomics-content">
          <motion.div 
            className="tokenomics-chart"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <div className="chart-container">
              <canvas ref={chartRef}></canvas>
              <div className="chart-center">
                <motion.div 
                  className="total-supply"
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ delay: 1.5 }}
                >
                  <div className="supply-number">1B</div>
                  <div className="supply-label">TERRA</div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          <div className="tokenomics-details">
            <motion.div 
              className="token-info"
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {tokenInfo.map((info, index) => (
                <motion.div 
                  key={info.label}
                  className="info-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.7 + index * 0.1 }}
                >
                  <span className="label">{info.label}</span>
                  <span className="value">{info.value}</span>
                </motion.div>
              ))}
            </motion.div>

            <motion.div 
              className="distribution-list"
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              {tokenomicsData.map((item, index) => (
                <motion.div 
                  key={item.label}
                  className="distribution-item"
                  initial={{ opacity: 0, x: 30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 1 + index * 0.1 }}
                  whileHover={{ 
                    scale: 1.05,
                    backgroundColor: `${item.color}10`
                  }}
                >
                  <div 
                    className="color-indicator" 
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="percentage">{item.percentage}%</span>
                  <span className="category">{item.label}</span>
                  <span className="amount">{item.amount}</span>
                </motion.div>
              ))}
            </motion.div>

            <motion.div 
              className="utility-section"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <h3>Token Utility</h3>
              <div className="utility-grid">
                <div className="utility-item">
                  <div className="utility-icon">💰</div>
                  <div className="utility-text">
                    <h4>Staking Rewards</h4>
                    <p>Earn passive income by staking TERRA tokens</p>
                  </div>
                </div>
                <div className="utility-item">
                  <div className="utility-icon">🗳️</div>
                  <div className="utility-text">
                    <h4>Governance Voting</h4>
                    <p>Participate in protocol decisions and proposals</p>
                  </div>
                </div>
                <div className="utility-item">
                  <div className="utility-icon">🔥</div>
                  <div className="utility-text">
                    <h4>Token Burns</h4>
                    <p>Regular burns to reduce supply and increase value</p>
                  </div>
                </div>
                <div className="utility-item">
                  <div className="utility-icon">🚀</div>
                  <div className="utility-text">
                    <h4>Ecosystem Access</h4>
                    <p>Access to exclusive features and early launches</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="tokenomics-bg">
        <div className="floating-coins">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="floating-coin"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -50, 0],
                rotate: [0, 360],
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 6 + Math.random() * 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Tokenomics;